import React, { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Camera, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

export const CONDITIONS = ["new", "ok", "needs_repair", "broken", "written_off"];

export function useLookups() {
  const { can } = useAuth();
  const [d, setD] = useState({ emps: [], projects: [], teams: [], warehouses: [], cats: [], units: [], depts: [] });
  useEffect(() => {
    const safe = (p, fb) => p.catch(() => ({ data: fb }));
    Promise.all([
      can("employees.view") ? safe(api.get("/employees"), []) : Promise.resolve({ data: [] }),
      can("projects.view") ? safe(api.get("/projects"), []) : Promise.resolve({ data: [] }),
      safe(api.get("/org/tree"), { teams: [], departments: [] }), safe(api.get("/warehouse/warehouses", { params: { page_size: 100 } }), { items: [] }),
      safe(api.get("/warehouse/categories"), []), safe(api.get("/config/units"), []),
    ]).then(([e, p, o, w, c, u]) => setD({ emps: e.data, projects: p.data.items || p.data, teams: o.data.teams || [], depts: o.data.departments || [], warehouses: w.data.items, cats: c.data, units: u.data }));
  }, [can]);
  return d;
}

export function Sel({ value, onChange, options, placeholder, testid, labelOf = (o) => o.name }) {
  const { tn } = useI18n();
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-10 w-full rounded-md border bg-[var(--card)] px-2 text-sm min-w-0">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.id} value={o.id}>{tn(labelOf(o)) || labelOf(o)}</option>)}
    </select>
  );
}

export function Field({ label, children }) {
  return <label className="block text-xs font-semibold text-[var(--text-3)] space-y-1 min-w-0"><span>{label}</span>{children}</label>;
}

export function FormDialog({ open, onClose, title, onSave, children, testid = "wh-dialog" }) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} data-testid={`${testid}-cancel`}>{t("cancel")}</Button>
          <Button onClick={onSave} className="bg-[var(--amber)] text-white" data-testid={`${testid}-save`}>{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Pager({ page, total, pageSize, onPage, testid = "pager" }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between text-sm mt-3" data-testid={testid}>
      <span className="text-[var(--text-3)]">{total}</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft size={16} /></Button>
        <span className="font-mono-num">{page}/{pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight size={16} /></Button>
      </div>
    </div>
  );
}

export function PhotoUploader({ ids = [], onChange, entityId = "", testid = "wh-photos" }) {
  const { t } = useI18n();
  const up = async (file) => {
    if (file.size > 10 * 1024 * 1024) return toast.error(t("file_too_large"));
    const fd = new FormData(); fd.append("file", file); fd.append("entity_type", "asset"); if (entityId) fd.append("entity_id", entityId);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange([...ids, data.id]); }
    catch { toast.error(t("upload_failed")); }
  };
  return (
    <div className="sm:col-span-2" data-testid={testid}>
      <div className="text-xs font-semibold text-[var(--text-3)] mb-1">{t("photos")}</div>
      <div className="flex gap-2 flex-wrap">
        {ids.map((id) => (
          <div key={id} className="relative w-20 h-20 tactical-card overflow-hidden">
            <img src={fileUrl(id)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange(ids.filter((x) => x !== id))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded p-0.5" data-testid={`${testid}-del-${id}`}><Trash2 size={12} /></button>
          </div>
        ))}
        <label className="w-20 h-20 tactical-card flex flex-col items-center justify-center text-xs cursor-pointer gap-1">
          <Camera size={18} /> {t("upload")}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && up(e.target.files[0])} data-testid={`${testid}-input`} />
        </label>
      </div>
    </div>
  );
}

export const nameOf = (list, id) => list.find((x) => x.id === id)?.name || "";

export function NoAccess() {
  const { t } = useI18n();
  return <div className="tactical-card p-6 text-center" data-testid="no-access"><div className="font-display font-bold text-lg">{t("no_access")}</div><div className="text-sm text-[var(--text-3)] mt-1">{t("no_access_hint")}</div></div>;
}
